import React from 'react'

import { InputField, FormFieldStatus } from '@gousto-internal/citrus-react'
import CheckBox from 'Form/CheckBox'
import { CTA } from 'goustouicomponents'

import Form from 'components/Form'
import { Recaptcha } from 'components/Recaptcha'
import routes from 'config/routes'
import { isSecretPingdomEmail } from 'utils/recaptcha'

import css from './LoginForm.css'

interface LoginFormProps {
  isAuthenticated?: boolean
  isAuthenticating?: boolean
  onSubmit: (data: {
    email: string
    password: string
    rememberMe: boolean
    recaptchaToken: string | null
  }) => void
  statusText?: string | boolean
}

interface LoginFormState {
  email: string
  password: string
  remember: boolean
  emailValid: boolean
  passwordValid: boolean
  showValidationError: boolean
  recaptchaValue: string | null
  emailTouched: boolean
  passwordTouched: boolean
}

class LoginForm extends React.PureComponent<LoginFormProps, LoginFormState> {
  recaptchaElement: any

  constructor(props: LoginFormProps) {
    super(props)
    this.state = {
      email: '',
      password: '',
      remember: true,
      emailValid: false,
      passwordValid: false,
      showValidationError: false,
      recaptchaValue: null,
      emailTouched: false,
      passwordTouched: false,
    }
  }

  // Deprecated lifecycle method, but keeping for parity
  UNSAFE_componentWillReceiveProps = (nextProps: LoginFormProps) => {
    if (nextProps.statusText) {
      this.setState({
        showValidationError: true,
        recaptchaValue: null,
      })

      if (this.recaptchaElement) {
        this.recaptchaElement.reset()
      }
    }
  }

  componentWillUnmount() {
    this.setState({ showValidationError: false })
  }

  handleSubmit = (e: any) => {
    e.preventDefault()

    if (this.captchaNeedsExecuting()) {
      this.recaptchaElement.execute()
    } else {
      this.processLogin()
    }
  }

  processLogin = () => {
    // shouldn't reach here, but just in case
    if (this.captchaNeedsExecuting()) {
      return
    }

    const { email, password, remember, emailValid, passwordValid, recaptchaValue } = this.state
    const { onSubmit } = this.props

    if (emailValid && passwordValid) {
      onSubmit({ email, password, rememberMe: remember, recaptchaToken: recaptchaValue })
    }
  }

  emailChanges = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const isValid = value.length > 0 && /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i.test(value)
    this.setState({ email: value, emailValid: isValid, emailTouched: true })
  }

  passwordChanges = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const isValid = value.length > 0
    this.setState({ password: value, passwordValid: isValid, passwordTouched: true })
  }

  checkboxChanges = (checked: boolean) => {
    this.setState({ remember: checked })
  }

  captchaChanges = (token: string | null) => {
    // only call processLogin callback if the captcha value isn't null (otherwise this is being called due to the captcha expiring)
    const callback = token === null ? undefined : this.processLogin
    this.setState({ recaptchaValue: token }, callback)
  }

  renderConfirmMessage = () => (
    <div className={css.confirmMsg}>
      You are now Logged in
      <span className={css.confirm} />
    </div>
  )

  captchaNeedsExecuting = () => {
    const { email, recaptchaValue } = this.state
    const secretEmailEntered = isSecretPingdomEmail(email)

    // prevent errors from breaking the page if the captcha isn't loaded for whatever reason
    const captchaElementNotOnPage = !this.recaptchaElement
    if (secretEmailEntered || captchaElementNotOnPage) {
      return false
    }

    return recaptchaValue === null
  }

  setCaptchaRef = (el: any) => {
    this.recaptchaElement = el
  }

  renderLoginForm = () => {
    const { isAuthenticating, statusText } = this.props
    const {
      email,
      password,
      remember,
      emailValid,
      passwordValid,
      showValidationError,
      emailTouched,
      passwordTouched,
    } = this.state

    return (
      <Form onSubmit={this.handleSubmit} method="post" data-testing="loginForm" noValidate>
        <div className={css.inputContainer}>
          <InputField
            id="email"
            label="Email"
            onChange={this.emailChanges}
            placeholder="Your email"
            required
            type="email"
            data-testing="inputLoginEmail"
            value={email}
            status={!emailTouched || emailValid ? undefined : FormFieldStatus.Error}
            validationMessage={
              !emailTouched || emailValid ? undefined : 'Please enter a valid email address'
            }
          />
        </div>
        <div className={css.inputContainer}>
          <InputField
            id="password"
            label="Password"
            onChange={this.passwordChanges}
            placeholder="Your password"
            required
            type="password"
            data-testing="inputLoginPassword"
            value={password}
            status={!passwordTouched || passwordValid ? undefined : FormFieldStatus.Error}
            validationMessage={
              !passwordTouched || passwordValid ? undefined : 'This field is required'
            }
          />
        </div>
        <div className={css.appAwarenessBottomContainer}>
          <div className={css.loginOptionsContainer}>
            <div className={css.resetFormContainer}>
              <a href={routes.client.resetForm} className={css.link}>
                Forgot your password?
              </a>
            </div>
            <div className={css.rememberMe}>
              <CheckBox
                dataTesting="loginCheckbox"
                label="Remember me"
                textSize="Medium"
                onChange={this.checkboxChanges}
                checked={remember}
              />
            </div>
          </div>
          {showValidationError && (
            <div className={css.error} data-testing="loginErrMsg">
              <span className={css.errorIcon} />
              {statusText}
            </div>
          )}
          <div>
            <Recaptcha ref={this.setCaptchaRef} onChange={this.captchaChanges} />
          </div>
          <CTA
            testingSelector="loginFormSubmit"
            onClick={this.handleSubmit}
            isLoading={isAuthenticating}
            isFullWidth
          >
            Log in
          </CTA>
        </div>
      </Form>
    )
  }

  render() {
    const { isAuthenticated } = this.props

    return (
      <div data-testing="loginModal">
        {isAuthenticated ? this.renderConfirmMessage() : this.renderLoginForm()}
      </div>
    )
  }
}

export { LoginForm }
